import { Locale } from '@/common'
import { mergeStrings } from '@/logic/mergeStrings'
import Data, { DataClass } from 'data'
import {
  checkCondition,
  conditionOverlap,
  forceCondition,
  formatValue,
  influenceFlags,
  isZero,
  modStats,
  statsExtend
} from 'logic/mods'
import { ModDefinition, Range, TranslationDefinition, TranslationDomain, TranslationRange } from './types'

type RoundType = 'floor' | 'ceil' | 'round'
type QuantifierType = 'passive' | undefined

class Quantifier {
  step: number
  round?: number

  constructor(
    public multiply: number,
    public add: number,
    round: number | undefined,
    public type: QuantifierType,
    public length: number
  ) {
    this.multiply = multiply
    this.add = add
    this.step = this.multiply
    this.type = type
    if (round !== undefined) {
      this.round = Math.pow(10, round)
      this.step = Math.round(this.step * this.round) / this.round
    } else {
      this.round = 1
      this.step = 1
    }
  }

  /**
   * Apply quantifier
   *
   * @param {number|object} value numeric value or min/max object
   * @param {string} [roundType] which rounding method to use (floor/ceil/round)
   * @returns {number|object}
   */
  apply(value: number, roundType?: RoundType): number | string
  apply(value: Range, roundType?: RoundType): Range | string
  apply(value: number | Range, roundType?: RoundType): number | Range | string
  apply(value: number | Range, roundType: RoundType = 'round'): number | Range | string {
    if (this.type && typeof value === 'object' && value.min === value.max) {
      value = value.max
    }
    if (typeof value === 'object') {
      if (this.type === 'passive') return '<Passive>'
      if (this.type === 'skill') return '<Skill>'
      const min = this.apply(value.min, this.multiply > 0 ? 'floor' : 'ceil') as number
      const max = this.apply(value.max, this.multiply > 0 ? 'ceil' : 'floor') as number
      return { min: Math.min(min, max), max: Math.max(min, max) }
    }
    if (this.type === 'passive') {
      if (value === -1) return '<Keystone>'
      if (value === -2) return '<Random Ascendancy Notable>'
      if (value === -3) return '<Notable>'
      const node = Data.passive_tree.nodes[value]
      if (!node) return '<Passive>'
      const skill = Data.passive_skills[node.skill_id]
      if (!skill) return '<Passive>'
      return skill.name
    }
    value = value * this.multiply + this.add
    if (this.round) value = Math[roundType || 'round'](value * this.round) / this.round
    return value
  }

  /**
   * Apply reverse quantifier
   *
   * @param {number|object} value numeric value or min/max object
   * @returns {number|object}
   */
  reverse(value: number | string): number
  reverse(value: Range): Range
  reverse(value: number | Range | string): number | Range
  reverse(value: number | Range | string): number | Range {
    if (typeof value === 'object') {
      if (this.type) return { min: 0, max: 0 }
      const min = this.reverse(value.min)
      const max = this.reverse(value.max)
      return { min: Math.min(min, max), max: Math.max(min, max) }
    }
    if (this.type === 'passive') {
      const key = Object.entries(Data.passive_tree.nodes).find(
        ([, node]) => Data.passive_skills[node.skill_id].name === value
      )
      if (key) return Number(key[0])
      return 0
    }
    return Math.round((Number(value) - this.add) / this.multiply)
  }
}

class StatRegExp {
  static brkRegex = new RegExp(/^([-+]?)\((-?\d+(?:\.\d+)?)(?: to |-)(-?\d+(?:\.\d+)?)\)$/)

  private list: number[] = []
  private re: RegExp

  constructor(string: string, format: string[], private types?: QuantifierType[]) {
    string = string.replace(/[$-.[-^?|]/g, '\\$&')
    string = string.replace(/{(\d+)}/g, (_, m) => {
      const index = parseInt(m, 10)
      this.list.push(index)
      if (types?.[index]) return '(.*)'
      let res = format[index][0] === '+' ? '([-+]?' : '(-?'
      res += '(?:\\(-?\\d+(?:\\.\\d+)?(?: to |-)-?\\d+(?:\\.\\d+)?\\)|\\d+(?:\\.\\d+)?))'
      if (format[index].includes('%')) res += '%'
      return res
    })
    this.re = new RegExp(`^${string}$`, 'i')
  }

  parse(text: string) {
    const res = this.re.exec(text)
    if (!res) return
    const ans: (number | Range | string)[] = []
    this.list.forEach((dst, src) => {
      if (this.types?.some(Boolean)) {
        ans[dst] = res[src + 1]
        return
      }
      const brk = StatRegExp.brkRegex.exec(res[src + 1])
      if (brk) {
        const sgn = brk[1] === '-' ? -1 : 1
        ans[dst] = {
          min: sgn * parseFloat(brk[2]),
          max: sgn * parseFloat(brk[3])
        }
      } else {
        ans[dst] = parseFloat(res[src + 1])
      }
    })
    return ans
  }
}

type TranslationString = {
  condition: TranslationRange[]
  string: string
  format: string[]
  regex: StatRegExp
  index_handlers: Quantifier[]
  reminder_strings?: string[]
}

export class Translation {
  ids: string[]
  hidden?: boolean;
  [Locale]: TranslationString[]
  best_index = 0

  constructor(src: TranslationDefinition) {
    this.ids = src.ids
    this.hidden = src.hidden
    let best_ignored = 100,
      best_negated = 100
    this[Locale] = src[Locale].map((opt, opt_index) => {
      let ignored = 0,
        negated = 0
      const string = opt.string.trim()
      const index_handlers = opt.index_handlers.map((handlers, index) => {
        let multiply = 1,
          add = 0,
          round = undefined
        let type: QuantifierType
        for (const id of handlers) {
          if (id === 'passive_hash') type = 'passive'
          const quant = Data.quantifiers[id]
          if (!quant) continue
          const qm = quant.divide ? 1.0 / quant.divide : quant.multiply || 1
          multiply *= qm
          add = add * qm + (quant.add || 0)
          round = quant.round
        }
        if (opt.format[index] === 'ignore') ignored += 1
        else if (multiply < 0) negated += 1
        if (type) {
          const stat = Data.stats[src.ids[index]]
          if (stat) stat.passive = true
        }
        return new Quantifier(multiply, add, round, type, handlers.length)
      })
      const str: TranslationString = {
        ...opt,
        string,
        regex: new StatRegExp(
          string,
          opt.format,
          index_handlers.map((h) => h.type)
        ),
        index_handlers
      }

      if (ignored < best_ignored || (ignored === best_ignored && negated < best_negated)) {
        this.best_index = opt_index
        best_ignored = ignored
        best_negated = negated
      }

      return str
    })
  }

  choose(values: (number | Range)[]) {
    let bestOverlap = 0
    let bestItem: TranslationString | undefined
    for (const item of this[Locale]) {
      let overlap = 0
      let valid = true
      item.condition.forEach((cond, index) => {
        const ovr = conditionOverlap(cond, values[index] ?? 0)
        if (!ovr) valid = false
        else overlap += ovr
      })
      if (valid && overlap > bestOverlap) {
        bestItem = item
        bestOverlap = overlap
      }
    }
    return bestItem
  }
  generic() {
    const scores = this[Locale].map((item) => {
      let score = 0
      for (let i = 0; i < item.condition.length; ++i) {
        if (item.format[i] === 'ignore') {
          score += 1000000
        } else if (item.index_handlers[i]?.length) {
          score += item.index_handlers[i].length * 100000
        } else {
          const { min, max } = item.condition[i]
          if (min != null) score -= min
        }
      }
      return score
    })
    let ideal = 0
    for (let i = 1; i < scores.length; ++i) {
      if (scores[i] < scores[ideal]) {
        ideal = i
      }
    }
    return this[Locale][ideal]
  }
}

class Domain {
  static searchRegEx = /[-+]?(?:\(-?\d+(?:\.\d+)?(?: to |-)-?\d+(?:\.\d+)?\)|\d+(?:\.\d+)?)/g
  static parseLineWithTrans(line: string, trans: Translation, opt: TranslationString) {
    const out = opt.regex.parse(line)
    if (!out) return
    const result: Record<string, number | Range> = {}
    let invalid = false
    trans.ids.forEach((id, index) => {
      let value: number | Range
      if (opt.format[index] === 'ignore') {
        // if a value is not part of the translation, we need to make one up
        value = forceCondition(opt.condition[index], 1)
      } else {
        value = opt.index_handlers[index].reverse(out[index])
        if (!checkCondition(opt.condition[index], value)) {
          invalid = true
          return
        }
      }
      if (!isZero(value)) {
        result[id] = value
      }
    })
    return invalid ? undefined : result
  }

  [key: number]: Translation

  private from_stat!: Record<
    string,
    {
      id: number
      index: number
    }
  >
  private lookup!: Record<
    string,
    {
      id: number
      index: number
      count: number
    }[]
  >
  private lookup_passive!: {
    id: number
    index: number
  }[]
  private lookup_multi!: Record<
    string,
    {
      id: number
      index: number
      count: number
    }[]
  >

  init() {
    Object.defineProperties(this, {
      from_stat: {
        enumerable: false,
        value: Object.create(this.from_stat ?? null)
      },
      lookup: { enumerable: false, value: Object.create(this.lookup ?? null) },
      lookup_multi: {
        enumerable: false,
        value: Object.create(this.lookup_multi ?? null)
      },
      lookup_passive: {
        enumerable: false,
        value: this.lookup_passive?.slice() ?? []
      }
    })

    for (const [id, trans] of Object.entries(this).map(([id, trans]) => [Number(id), trans as Translation] as const)) {
      //if (!trans.hidden) {
      trans.ids.forEach((stat, index) => (this.from_stat[stat] = { id, index }))
      //}

      trans[Locale].forEach((opt, index) => {
        const lookupString = (src: string) =>
          src
            .replace(/[-+]?(?:{(\d+)}|\d+(?:\.\d+)?)/g, (_, idx) =>
              idx ? '$' + (opt.format[parseInt(idx, 10)].includes('%') ? '%' : '') : '$'
            )
            .toLowerCase()

        const count = opt.format.filter((f) => f !== 'ignore').length

        const key = lookupString(opt.string)
        if (!Object.hasOwnProperty.call(this.lookup, key)) {
          this.lookup[key] = this.lookup[key]?.slice() || []
        }
        this.lookup[key].push({ id, index, count })
        if (opt.string.includes('\n')) {
          const keyMulti = lookupString(opt.string.split('\n')[0])
          if (!Object.hasOwnProperty.call(this.lookup_multi, keyMulti)) {
            this.lookup_multi[keyMulti] = this.lookup_multi[keyMulti]?.slice() || []
          }
          this.lookup_multi[keyMulti].push({ id, index, count })
        }
        if (opt.index_handlers.some((h) => h.type)) {
          this.lookup_passive.push({ id, index })
        }
      })
    }
    for (const arr of Object.values(this.lookup))
      arr.sort((a, b) => (a.count === b.count ? a.index - b.index : a.count - b.count))
    for (const arr of Object.values(this.lookup_multi))
      arr.sort((a, b) => (a.count === b.count ? a.index - b.index : a.count - b.count))
  }

  hasStat(id: string) {
    return this.from_stat[id] != null
  }

  /**
   * Builds a set of stat translations containing given stats.
   *
   * @param {Object} stats stats map {id: value}
   */
  maskFromStatList<T>(stats: Record<string, T>) {
    const lines: Record<number, T[]> = {}
    for (const [id, value] of Object.entries(stats)) {
      const line = this.from_stat[id]
      if (!line) continue
      lines[line.id] = lines[line.id] || this[line.id].ids.map(() => 0)
      lines[line.id][line.index] = value
    }
    return lines
  }

  /**
   * Parse stat line, optionally within a subset of possible lines.
   * Returns a stat Map, with stat id's as keys, and numbers or ranges as values.
   * Returns undefined if no match was found.
   * @param {string} line input text line
   * @param {Object?} [mask] optionally mask which translations are allowed
   */
  parseLine(line: string, mask?: Record<number, any>) {
    // fast lookup by replacing numbers and ranges with $'s
    line = line.trim()
    const search = line.replace(Domain.searchRegEx, '$').toLowerCase()
    const lookup = this.lookup[search]
    if (!lookup) return
    for (let i = lookup.length - 1; i >= 0; --i) {
      // check every matched translation line
      if (mask && !mask[lookup[i].id]) continue
      const trans = this[lookup[i].id]
      const opt = trans[Locale][lookup[i].index]
      const result = Domain.parseLineWithTrans(line, trans, opt)
      if (result) return result
    }
  }

  /**
   * Parse an array of text lines, where some stats might span multiple lines,
   * filtered by a mask of stat_translations. Returns stat object.
   * Lines that were not parsed will be filled into `unparsed` array.
   * @param {string[]} lines multiple input lines
   * @param {Object?} [mask] optional translation mask
   * @param {array} [unparsed] receives list of lines with no matching translation
   * @param {object} [ambiguous] receives list of ambiguous input lines
   */
  parseMultiLine({
    lines,
    mask,
    statFilter,
    unparsed,
    ambiguous
  }: {
    lines: string[]
    mask?: Record<number, any>
    statFilter?: (id: string) => any
    unparsed?: string[]
    ambiguous?: Record<string, number | Range>[][]
  }) {
    // trim lines
    lines = lines.map((line) => line.trim())
    const total: Record<string, number | Range> = {}
    for (let i = 0; i < lines.length; ++i) {
      let result: Record<string, number | Range> | undefined
      const search = lines[i].replace(Domain.searchRegEx, '$').toLowerCase()

      const options: {
        length: number
        stats: Record<string, number | Range>
      }[] = []

      // try multiple lines first
      let lookup = this.lookup_multi[search]
      if (lookup) {
        for (let j = lookup.length - 1; j >= 0; --j) {
          if (mask && !mask[lookup[j].id]) continue
          const trans = this[lookup[j].id]
          if (trans.hidden) continue
          if (statFilter && !trans.ids.some((id) => statFilter(id))) continue
          const opt = trans[Locale][lookup[j].index]
          const numLines = opt.string.split('\n').length
          if (i + numLines <= lines.length) {
            const combined = lines.slice(i, i + numLines).join('\n')
            const stats = Domain.parseLineWithTrans(combined, trans, opt)
            if (stats) {
              options.push({ stats, length: numLines })
              if (!ambiguous) break
            }
          }
        }
      }
      // try single line
      if (!options.length) {
        lookup = this.lookup[search]
        if (lookup) {
          for (let j = lookup.length - 1; j >= 0; --j) {
            if (mask && !mask[lookup[j].id]) continue
            const trans = this[lookup[j].id]
            if (trans.hidden) continue
            if (statFilter && !trans.ids.some((id) => statFilter(id))) continue
            const opt = trans[Locale][lookup[j].index]
            const stats = Domain.parseLineWithTrans(lines[i], trans, opt)
            if (stats) {
              options.push({ stats, length: 1 })
              if (!ambiguous) break
            }
          }
        }
      }
      // try passives
      if (!options.length) {
        for (const p of this.lookup_passive) {
          const trans = this[p.id]
          if (trans.hidden) continue
          const opt = trans[Locale][p.index]
          const numLines = opt.string.split('\n').length
          if (i + numLines <= lines.length) {
            const combined = lines.slice(i, i + numLines).join('\n')
            const stats = Domain.parseLineWithTrans(combined, trans, opt)
            if (stats) {
              options.push({ stats, length: 1 })
              // Don't break out here because we need to parse them as different ids
              // if (!ambiguous) break
            }
          }
        }
      }

      if (options.length) {
        const maxLength = Math.max(...options.map((opt) => opt.length))
        const stats = options.filter((opt) => opt.length === maxLength).map((opt) => opt.stats)

        // We don't want to overlap stats if possible
        let index = stats.findIndex((stats) => Object.keys(stats).every((id) => !total[id]))
        if (index < 0) index = 0
        if (index > 0) stats.unshift(...stats.splice(index, 1))

        if (ambiguous) ambiguous.push(stats)
        statsExtend(total, stats[0])
      } else {
        if (unparsed) unparsed.push(lines[i])
      }
    }
    return total
  }

  /**
   * Converts a stat map into a list of description lines, as they would appear on an item.
   * If `keys` is true, will return an map with indices being translation id's instead.
   * @param {Object} stats Stats map {id: value}
   * @param {boolean} [keys=false] Use translation line IDs as result keys
   * @param {boolean} [min=false] Only return value ranges, no text
   */
  formatStats(
    stats: Record<string, number | Range>,
    keys: boolean = false,
    min: boolean = false,
    reminders?: string[][]
  ) {
    // build a list of values for each used translation
    const lines = this.maskFromStatList(stats)

    const result = []
    for (const [index, values] of Object.entries(lines).map(([index, values]) => [Number(index), values] as const)) {
      if (this[index].hidden) continue
      if (values.every((val) => isZero(val))) continue

      const stat = this[index]
      let trans = stat.choose(values)
      if (!trans) continue

      let string = trans.string

      const rules = stat[Locale].filter((rule) =>
        rule.condition.every((cond, index) => checkCondition(cond, values[index] ?? 0, false))
      )
      const ignoredIds = stat.ids.filter((id, index) => {
        if (trans!.format[index] !== 'ignore') return false
        const cond = trans!.condition[index]
        if (cond.min == null && cond.max == null) return false
        return rules.some(
          (r) =>
            r.format[index] === 'ignore' && (r.condition[index].min !== cond.min || r.condition[index].max !== cond.max)
        )
      })
      if (ignoredIds.length && values.some((v) => typeof v === 'object')) {
        const ignored = stat.ids.map((id, index) => rules.every((rule) => rule.format[index] === 'ignore'))
        const filtered = rules.filter((rule) =>
          stat.ids.every((id, index) => ignored[index] || rule.format[index] !== 'ignore')
        )
        if (filtered.length) {
          trans = filtered[0]
          string = mergeStrings(filtered.map((f) => f.string))
        }
      }

      if (min) {
        string = string.replace(/^[^{]*/, '')
        string = string.replace(/[^}]*$/, '')
      }
      const text = string.replace(/{(\d+)}/g, (_, m) => {
        const idx = parseInt(m, 10)
        let value = trans!.index_handlers[idx].apply(values[idx] || 0)
        // empty range is displayed as a single value
        if (typeof value === 'object' && value.min === value.max) value = value.min
        return formatValue(value, min ? 'min' : trans!.format[idx])
      })
      if (reminders && trans.reminder_strings) {
        reminders[keys ? index : result.length] = trans.reminder_strings
      }
      if (keys) {
        result[index] = text
      } else {
        result.push(text)
      }
    }

    return result
  }

  /**
   * Format a single item mod as a string describing its stats/ranges
   * @param {object} mod Mod info
   * @param {boolean} [list=false] Return a list of lines instead of a single string
   */
  formatMod(mod: ModDefinition, list = false) {
    let result = this.formatStats(modStats(mod))
    if (list) return result
    let str = result.join(' / ')
    if (mod.is_essence_only) str += ` (Essence)`
    for (const { flag, name } of influenceFlags) {
      if (mod.influence & flag) str += ` (${name})`
    }
    return result
  }
}

const Trans: Record<string, Domain> = { all: new Domain() }

export function initTrans(Data: DataClass) {
  let Index = 1
  const sourceDomains: Record<string, TranslationDomain> = {}
  for (const domain of Data.translations) {
    sourceDomains[domain.name] = domain
  }
  Data.translations.forEach(function initDomain(domain: TranslationDomain) {
    if (Trans[domain.name]) return Trans[domain.name]
    if (domain.parent) {
      Trans[domain.name] = Object.create(initDomain(sourceDomains[domain.parent]))
    } else {
      Trans[domain.name] = new Domain()
    }

    for (const trans of domain.data) {
      Trans.all[Index] = Trans[domain.name][Index] = new Translation(trans)
      ++Index
    }
    Trans[domain.name].init()
    return Trans[domain.name]
  })
}

export { Domain, Quantifier }
export default Trans
